import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import {
  updateIsInviting,
  updateIsAcceptingInvitation,
  updateIsCreatingUnlockRequest,
  updateIsProcessingUnlockRequest,
  updateIsFetchingUnlockRequests,
  updateShowInviteDrawer,
  updateShowCreateUnlockRequestDrawer,
  updateShowEndPartnershipConfirm,
  updateShowApproveUnlockRequestConfirm,
  updateShowRejectUnlockRequestConfirm,
  updateInvitationLinkHasCustomError
} from './slice';
import Endpoints from 'constants/endpoints';
import { HTTP_STATS_CODE } from 'constants/general';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import {
  AccountabilityBuddy,
  InviteBuddyPayload,
  CreateUnlockRequestPayload,
  GetUnlockRequestsQueryParams,
  UnlockRequestsResponse
} from 'interfaces/accountability-buddy.interface';
import { AxiosError } from 'axios';
import { isForbiddenError } from 'utils/api-error.util';

export const fetchCurrentBuddy = createAsyncThunk(
  'accountabilityBuddy/fetch_current_buddy',
  async () => {
    try {
      const { data } = await focusBearApi.get<
        AccountabilityBuddy | AccountabilityBuddy[]
      >(Endpoints.ACCOUNTABILITY_BUDDY.BASE);

      if (!data) return [];
      return Array.isArray(data) ? data : [data];
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === HTTP_STATS_CODE.NOT_FOUND) {
        return [];
      }
      captureException(JSON.stringify(error));
      throw error;
    }
  }
);

export const fetchInvitations = createAsyncThunk(
  'accountabilityBuddy/fetch_invitations',
  async () => {
    try {
      const { data } = await focusBearApi.get<AccountabilityBuddy[]>(
        `${Endpoints.ACCOUNTABILITY_BUDDY.BASE}/invitations`
      );
      return Array.isArray(data) ? data : [];
    } catch (error) {
      captureException(JSON.stringify(error));
      return [];
    }
  }
);

export const fetchUnlockRequests = createAsyncThunk(
  'accountabilityBuddy/fetch_unlock_requests',
  async (params: GetUnlockRequestsQueryParams | undefined, { dispatch }) => {
    try {
      dispatch(updateIsFetchingUnlockRequests(true));
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) {
        queryParams.append('page', String(params.page));
      }
      if (params?.take !== undefined) {
        queryParams.append('take', String(params.take));
      }
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.created_from) {
        queryParams.append('created_from', params.created_from);
      }
      if (params?.created_to) {
        queryParams.append('created_to', params.created_to);
      }
      if (params?.role) {
        queryParams.append('role', params.role);
      }

      const queryString = queryParams.toString();
      const url = queryString
        ? `${Endpoints.ACCOUNTABILITY_BUDDY.UNLOCK_REQUESTS}?${queryString}`
        : Endpoints.ACCOUNTABILITY_BUDDY.UNLOCK_REQUESTS;
      const { data } = await focusBearApi.get<UnlockRequestsResponse>(url);

      // Ensure meta has all required fields with defaults
      const meta = data?.meta || {};
      return {
        data: Array.isArray(data?.data) ? data.data : [],
        meta: {
          page: meta.page ?? params?.page ?? 1,
          take: meta.take ?? params?.take ?? 10,
          order: meta.order ?? 'ASC',
          itemCount: meta.itemCount ?? 0,
          pageCount: meta.pageCount ?? 1,
          hasNextPage: meta.hasNextPage ?? false,
          hasPreviousPage: meta.hasPreviousPage ?? false
        }
      };
    } catch (error) {
      captureException(JSON.stringify(error));
      return {
        data: [],
        meta: {
          page: params?.page || 1,
          take: params?.take || 10,
          order: 'ASC',
          itemCount: 0,
          pageCount: 1,
          hasNextPage: false,
          hasPreviousPage: false
        }
      };
    } finally {
      dispatch(updateIsFetchingUnlockRequests(false));
    }
  }
);

export const inviteBuddy = createAsyncThunk(
  'accountabilityBuddy/invite_buddy',
  async (payload: InviteBuddyPayload, { dispatch }) => {
    try {
      dispatch(updateIsInviting(true));
      const { status } = await focusBearApi.post(
        Endpoints.ACCOUNTABILITY_BUDDY.INVITE,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        dispatch(updateShowInviteDrawer(false));
        dispatch(fetchCurrentBuddy());
        toast.success(t('accountability_buddy.invitation_sent_successfully'));
      } else {
        toast.error(t('accountability_buddy.could_not_send_invitation'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsInviting(false));
    }
  }
);

export const acceptInvitation = createAsyncThunk(
  'accountabilityBuddy/accept_invitation',
  async (token: string, { dispatch }) => {
    try {
      dispatch(updateIsAcceptingInvitation(true));
      dispatch(
        updateInvitationLinkHasCustomError({
          hasError: false,
          message: ''
        })
      );
      const { status } = await focusBearApi.post(
        Endpoints.ACCOUNTABILITY_BUDDY.ACCEPT_INVITATION_FROM_EMAIL,
        { token }
      );
      if (status === HTTP_STATS_CODE.CREATED || status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(fetchCurrentBuddy());
        dispatch(fetchInvitations());
        toast.success(
          t('accountability_buddy.invitation_accepted_successfully')
        );
      } else {
        dispatch(
          updateInvitationLinkHasCustomError({
            hasError: true,
            message: t('could_not_process_the_request')
          })
        );
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      const errorData = error as AxiosError;
      const errorMessage = errorData?.response?.data;
      if (typeof errorMessage === 'string') {
        dispatch(
          updateInvitationLinkHasCustomError({
            hasError: true,
            message: errorMessage
          })
        );
      } else {
        dispatch(
          updateInvitationLinkHasCustomError({
            hasError: true,
            message: t('could_not_process_the_request')
          })
        );
        toast.error(t('could_not_process_the_request'));
      }
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsAcceptingInvitation(false));
    }
  }
);

export const rejectInvitation = createAsyncThunk(
  'accountabilityBuddy/reject_invitation',
  async (buddyId: string, { dispatch }) => {
    try {
      const { status } = await focusBearApi.post(
        `${Endpoints.ACCOUNTABILITY_BUDDY.BASE}/reject-invitation`,
        { accountability_buddy_id: buddyId }
      );
      if (
        status === HTTP_STATS_CODE.CREATED ||
        status === HTTP_STATS_CODE.SUCCESS
      ) {
        Promise.allSettled([
          dispatch(fetchCurrentBuddy()),
          dispatch(fetchInvitations())
        ]);
        toast.success(t('accountability_buddy.invitation_rejected'));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    }
  }
);

export const acceptReceivedInvitation = createAsyncThunk(
  'accountabilityBuddy/accept_received_invitation',
  async (buddyId: string, { dispatch }) => {
    try {
      dispatch(updateIsAcceptingInvitation(true));
      const { status } = await focusBearApi.patch(
        Endpoints.ACCOUNTABILITY_BUDDY.ACCEPT_RECEIVED_INVITATION(buddyId)
      );
      if (status === HTTP_STATS_CODE.CREATED || status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(fetchCurrentBuddy());
        dispatch(fetchInvitations());
        toast.success(
          t('accountability_buddy.invitation_accepted_successfully')
        );
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      const errorData = error as AxiosError;
      const errorMessage = errorData?.response?.data;
      const isExpiredError =
        typeof errorMessage === 'string' &&
        errorMessage.includes('This invitation has expired');

      if (isExpiredError) {
        toast.error('This invitation has expired');
        dispatch(fetchInvitations());
      } else {
        toast.error(t('could_not_process_the_request'));
      }
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsAcceptingInvitation(false));
    }
  }
);

export const rejectReceivedInvitation = createAsyncThunk(
  'accountabilityBuddy/reject_received_invitation',
  async (buddyId: string, { dispatch }) => {
    try {
      dispatch(updateIsAcceptingInvitation(true));
      const { status } = await focusBearApi.patch(
        Endpoints.ACCOUNTABILITY_BUDDY.REJECT_RECEIVED_INVITATION(buddyId)
      );
      if (
        status === HTTP_STATS_CODE.CREATED ||
        status === HTTP_STATS_CODE.SUCCESS
      ) {
        dispatch(fetchCurrentBuddy());
        dispatch(fetchInvitations());
        toast.success(t('accountability_buddy.invitation_rejected'));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      const errorData = error as AxiosError;
      const errorMessage = errorData?.response?.data;
      const isExpiredError =
        typeof errorMessage === 'string' &&
        errorMessage.includes('This invitation has expired');

      if (isExpiredError) {
        toast.error('This invitation has expired');
        dispatch(fetchInvitations());
      } else {
        toast.error(t('could_not_process_the_request'));
      }
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsAcceptingInvitation(false));
    }
  }
);

export const endPartnership = createAsyncThunk(
  'accountabilityBuddy/end_partnership',
  async (buddyId: string, { dispatch }) => {
    try {
      dispatch(updateIsProcessingUnlockRequest(true));
      await focusBearApi.delete(Endpoints.ACCOUNTABILITY_BUDDY.DELETE(buddyId));
      dispatch(updateShowEndPartnershipConfirm({ show: false }));
      dispatch(fetchCurrentBuddy());
      toast.success(t('accountability_buddy.partnership_ended'));
    } catch (error) {
      // A managing parent's partnership is locked for the student; only the school can end it.
      toast.error(
        t(
          isForbiddenError(error)
            ? 'accountability_buddy.partnership_locked'
            : 'could_not_process_the_request'
        )
      );
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsProcessingUnlockRequest(false));
    }
  }
);

export const createUnlockRequest = createAsyncThunk(
  'accountabilityBuddy/create_unlock_request',
  async (payload: CreateUnlockRequestPayload, { dispatch }) => {
    try {
      dispatch(updateIsCreatingUnlockRequest(true));
      const { status } = await focusBearApi.post(
        Endpoints.ACCOUNTABILITY_BUDDY.UNLOCK_REQUEST,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        dispatch(updateShowCreateUnlockRequestDrawer(false));
        dispatch(fetchUnlockRequests());
        toast.success(t('accountability_buddy.unlock_request_created'));
      } else {
        toast.error(t('accountability_buddy.could_not_create_unlock_request'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsCreatingUnlockRequest(false));
    }
  }
);

export const approveUnlockRequest = createAsyncThunk(
  'accountabilityBuddy/approve_unlock_request',
  async (requestId: string, { dispatch, getState }) => {
    try {
      dispatch(updateIsProcessingUnlockRequest(true));
      const { status } = await focusBearApi.post(
        Endpoints.ACCOUNTABILITY_BUDDY.APPROVE_UNLOCK(requestId)
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(
          updateShowApproveUnlockRequestConfirm({
            show: false,
            requestId: undefined
          })
        );
        const state = getState() as { accountabilityBuddy: { unlockRequestFilters: GetUnlockRequestsQueryParams } };
        const filters = state.accountabilityBuddy.unlockRequestFilters;
        dispatch(
          fetchUnlockRequests({
            page: filters.page,
            take: filters.take,
            status: filters.status,
            created_from: filters.created_from,
            created_to: filters.created_to,
            role: filters.role
          })
        );
        toast.success(t('accountability_buddy.unlock_request_approved'));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsProcessingUnlockRequest(false));
    }
  }
);

export const approveUnlockRequestByToken = createAsyncThunk(
  'accountabilityBuddy/approve_unlock_request_by_token',
  async (token: string, { dispatch, getState }) => {
    try {
      dispatch(updateIsProcessingUnlockRequest(true));
      const { status } = await focusBearApi.post(
        Endpoints.ACCOUNTABILITY_BUDDY.APPROVE_UNLOCK_BY_TOKEN,
        { token }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        const state = getState() as { accountabilityBuddy: { unlockRequestFilters: GetUnlockRequestsQueryParams } };
        const filters = state.accountabilityBuddy.unlockRequestFilters;
        dispatch(
          fetchUnlockRequests({
            page: filters.page,
            take: filters.take,
            status: filters.status,
            created_from: filters.created_from,
            created_to: filters.created_to,
            role: filters.role
          })
        );
        toast.success(t('accountability_buddy.unlock_request_approved'));
      } else {
        throw new Error(t('could_not_process_the_request'));
      }
    } catch (error) {
      const errorData = error as AxiosError;
      const errorMessage = errorData?.response?.data;
      if (typeof errorMessage === 'string') {
        throw new Error(errorMessage);
      } else {
        throw new Error(t('could_not_process_the_request'));
      }
    } finally {
      dispatch(updateIsProcessingUnlockRequest(false));
    }
  }
);

export const rejectUnlockRequest = createAsyncThunk(
  'accountabilityBuddy/reject_unlock_request',
  async (requestId: string, { dispatch, getState }) => {
    try {
      dispatch(updateIsProcessingUnlockRequest(true));
      const { status } = await focusBearApi.post(
        Endpoints.ACCOUNTABILITY_BUDDY.REJECT_UNLOCK,
        { unlock_request_id: requestId }
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(
          updateShowRejectUnlockRequestConfirm({
            show: false,
            requestId: undefined
          })
        );
        const state = getState() as { accountabilityBuddy: { unlockRequestFilters: GetUnlockRequestsQueryParams } };
        const filters = state.accountabilityBuddy.unlockRequestFilters;
        dispatch(
          fetchUnlockRequests({
            page: filters.page,
            take: filters.take,
            status: filters.status,
            created_from: filters.created_from,
            created_to: filters.created_to,
            role: filters.role
          })
        );
        toast.success(t('accountability_buddy.unlock_request_rejected'));
      } else {
        toast.error(t('could_not_process_the_request'));
      }
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
    } finally {
      dispatch(updateIsProcessingUnlockRequest(false));
    }
  }
);
